import React, { FC, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import _sortBy from 'lodash/sortBy';
import _filter from 'lodash/filter';
import { gqlCountryEnum as COUNTRY, gqlSizeGenderEnum as SIZE_GENDER } from '@minecraft.graphql-types';
import ErrorMessage from '@minecraft.error-message';
import { useMeasurementUnitType } from '@minecraft.utils';
import { useDrawerGlobalLoader, useDrawerSearchFilterNameOrCategory } from '@minecraft.drawer';
import {
  generateSelectOptionsBasedOnField,
  FormItem,
  FiltersNameEnum,
  useSizesCategoryOptions,
  FilterDrawerType,
} from '@blocs.filter';
import { FiltersCategoryNameEnum } from '../../shared/types';
import { ClothingSizes } from '../../FilterDrawerCollection';
import { QUERY_CLOTHING_SIZES } from './ClothingSizesCategoryGraphQL';

enum UnitSystemEnum {
  cm = 'cm',
  inch = 'inch',
  inchRounded = 'inchRounded',
}

type ClotheSizeCounty = {
  shoeSizeCountry: string;
  dressSizeCountry: string;
  pantsSizeCountry: string;
};

const getClotheSizeCounty = (countryCode: string): ClotheSizeCounty => {
  switch (countryCode) {
    case COUNTRY.US:
      return {
        shoeSizeCountry: 'shoeSizeUS',
        dressSizeCountry: 'sizeUS',
        pantsSizeCountry: 'sizeUS',
      };
    case COUNTRY.AU:
      return {
        shoeSizeCountry: 'shoeSizeAU',
        dressSizeCountry: 'sizeAU',
        pantsSizeCountry: 'sizeAU',
      };
    case COUNTRY.GB:
      return {
        shoeSizeCountry: 'shoeSizeUK',
        dressSizeCountry: 'sizeUK',
        pantsSizeCountry: 'sizeUK',
      };
    default:
      return {
        shoeSizeCountry: 'shoeSizeEU',
        dressSizeCountry: 'sizeEU',
        pantsSizeCountry: 'sizeEU',
      };
  }
};

interface Props {
  type: FilterDrawerType;
  accountCountryCode: string;
}

const ClothingSizesCategory: FC<Props> = ({ type, accountCountryCode }) => {
  const { data, error, loading } = useQuery(QUERY_CLOTHING_SIZES);

  const { showInImperial } = useMeasurementUnitType(accountCountryCode);

  const sizesCategoryOptions = useSizesCategoryOptions();

  const clothingSizesOptions = useMemo(() => {
    const measureSystem = showInImperial ? UnitSystemEnum.inchRounded : UnitSystemEnum.cm;
    const { shoeSizeCountry, dressSizeCountry, pantsSizeCountry } = getClotheSizeCounty(accountCountryCode);
    const {
      busts = [],
      chestSizes = [],
      coats = [],
      cupSizes = [],
      dresses = [],
      generalSizes = [],
      gloves = [],
      hatSizes = [],
      hips = [],
      inseams = [],
      necks = [],
      pants = [],
      shoeModifier = [],
      shoeSizes = [],
      sleeves = [],
      tshirtSizes = [],
      underBusts = [],
      waists = [],
    } = data || {};

    const sortedShoeSizes = _sortBy(shoeSizes, 'sortOrder');

    const boysSizes = _filter(generalSizes, (size) => size.category.code === SIZE_GENDER.BOY);
    const girlsSizes = _filter(generalSizes, (size) => size.category.code === SIZE_GENDER.GIRL);
    const toddlersSizes = _filter(generalSizes, (size) => size.category.code === SIZE_GENDER.TODDLER);
    const infantsSizes = _filter(generalSizes, (size) => size.category.code === SIZE_GENDER.INFANT);
    const childrenShoeSizes = _filter(sortedShoeSizes, (size) => size.category.code === SIZE_GENDER.KID);

    return {
      sleevesOptions: generateSelectOptionsBasedOnField(
        showInImperial ? _filter(sleeves, (sleeve) => sleeve.showInImperialUI) : sleeves,
        measureSystem
      ),
      chestOptions: generateSelectOptionsBasedOnField(
        showInImperial ? _filter(chestSizes, (chest) => chest.showInImperialUI) : chestSizes,
        measureSystem
      ),
      necksOptions: generateSelectOptionsBasedOnField(
        showInImperial ? _filter(necks, (neck) => neck.showInImperialUI) : necks,
        measureSystem
      ),
      coatsOptions: generateSelectOptionsBasedOnField(coats, 'name'),
      maleShoeSizeOptions: generateSelectOptionsBasedOnField(
        _filter(sortedShoeSizes, (size) => size.category.code === SIZE_GENDER.MALE),
        shoeSizeCountry
      ).sort((first, second) => Number(first.label) - Number(second.label)),
      maleTshirtSizesOptions: generateSelectOptionsBasedOnField(
        _filter(tshirtSizes, (tShirt) => tShirt.category.code === SIZE_GENDER.MALE),
        'name'
      ),
      waistsOptions: generateSelectOptionsBasedOnField(
        _sortBy(showInImperial ? _filter(waists, (waist) => waist.showInImperialUI) : waists, 'label'),
        measureSystem
      ),
      inseamsOptions: generateSelectOptionsBasedOnField(
        showInImperial ? _filter(inseams, (inseam) => inseam.showInImperialUI) : inseams,
        measureSystem
      ),
      glovesOptions: generateSelectOptionsBasedOnField(
        showInImperial ? _filter(gloves, (glove) => glove.showInImperialUI) : gloves,
        measureSystem
      ),
      hatsOptions: generateSelectOptionsBasedOnField(
        hatSizes,
        showInImperial ? UnitSystemEnum.inch : UnitSystemEnum.cm
      ),
      shoeModifierOptions: generateSelectOptionsBasedOnField(shoeModifier, 'name'),
      dressSizeOptions: generateSelectOptionsBasedOnField(dresses, dressSizeCountry, true),
      bustsOptions: generateSelectOptionsBasedOnField(
        showInImperial ? _filter(busts, (bust) => bust.showInImperialUI) : busts,
        measureSystem
      ),
      underBustsOptions: generateSelectOptionsBasedOnField(
        showInImperial ? _filter(underBusts, (underBust) => underBust.showInImperialUI) : underBusts,
        measureSystem
      ),
      cupsOptions: generateSelectOptionsBasedOnField(cupSizes, 'name'),
      hipsOptions: generateSelectOptionsBasedOnField(
        showInImperial ? _filter(hips, (hip) => hip.showInImperialUI) : hips,
        measureSystem
      ),
      femalePantsSizeOptions: generateSelectOptionsBasedOnField(
        _filter(pants, (pant) => pant.gender.code === SIZE_GENDER.FEMALE),
        pantsSizeCountry,
        true
      ),
      femaleShoeSizeOptions: generateSelectOptionsBasedOnField(
        _filter(sortedShoeSizes, (size) => size.category.code === SIZE_GENDER.FEMALE),
        shoeSizeCountry
      ),
      femaleTshirtSizesOptions: generateSelectOptionsBasedOnField(
        _filter(tshirtSizes, (tShirt) => tShirt.category.code === SIZE_GENDER.FEMALE),
        'name'
      ),
      boysSizesOptions: generateSelectOptionsBasedOnField(boysSizes, 'name'),
      girlsSizesOptions: generateSelectOptionsBasedOnField(girlsSizes, 'name'),
      toddlersSizesOptions: generateSelectOptionsBasedOnField(toddlersSizes, 'name'),
      infantsSizesOptions: generateSelectOptionsBasedOnField(infantsSizes, 'name'),
      childrenShoeSizeOptions: generateSelectOptionsBasedOnField(childrenShoeSizes, shoeSizeCountry),
    };
  }, [showInImperial, data]);

  const { isCategoryMatchedSearch, assignOptionsToCategoryFilters } = useDrawerSearchFilterNameOrCategory(
    FiltersCategoryNameEnum.clothingSizes
  );

  useDrawerGlobalLoader(loading);

  useEffect(() => {
    assignOptionsToCategoryFilters({
      [FiltersNameEnum.sizesCategory]: sizesCategoryOptions,
    });
  }, []);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    isCategoryMatchedSearch && (
      <FormItem $fullWidth $marginValue={2}>
        <ClothingSizes showInImperial={showInImperial} type={type} optionsList={clothingSizesOptions} />
      </FormItem>
    )
  );
};

export default ClothingSizesCategory;
